import styles from "./DragPreview.module.css"

import { animate } from "motion"

// 1x1 transparent GIF used to suppress the browser's built-in (translucent) drag ghost.
// Kept attached to the DOM because Safari ignores detached setDragImage elements.
let spacer: HTMLImageElement | undefined

function getSpacer(): HTMLImageElement {
  if (!spacer) {
    spacer = document.createElement("img")
    spacer.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
    spacer.style.position = "fixed"
    spacer.style.top = "-10px"
    spacer.style.left = "-10px"
    spacer.style.width = "1px"
    spacer.style.height = "1px"
    document.body.appendChild(spacer)
  }
  return spacer
}

/**
 * Class on <body> while a node drag is in flight. unified-tiptap.css uses it to hide the
 * browser's native drag caret in the editors — the drop indicator line already shows
 * where the block will land.
 */
const DRAGGING_CLASS = "unified-tiptap-dragging"

const ROTATION_DEGREES = 3
const PICKUP_SCALE = 1.02
/** Spring for the "pick up" tilt + grow when the drag starts. */
const PICKUP_SPRING = { type: "spring", stiffness: 350, damping: 25 } as const
/** Pointer-following translate: LOCKED to the pointer (no spring). A spring here
    trails the pointer by design, and on Chrome every main-thread stall freezes the
    chase mid-flight while the hand keeps moving — the trail stretches into a visible
    "the card is behind me" lag. Firefox masked it only because its frames didn't
    stall. The pickup tilt/grow spring above keeps the Trello feel. */
const FOLLOW_LOCKED = { duration: 0 } as const

/**
 * Tears down the in-flight drag preview (element, listeners, body class), if any.
 * Assigned for the duration of a drag; null when no preview is active.
 */
let activeCleanup: (() => void) | null = null

/**
 * Replaces the browser's translucent native drag ghost with a Trello-style preview:
 * an opaque clone of the dragged node on a white card with a shadow that springs up
 * (slight tilt + grow) and follows the pointer for the duration of the drag.
 *
 * Call from a dragstart handler. Only the ghost is replaced — ProseMirror's drag logic
 * (view.dragging, drop handling, drop cursor) runs as usual.
 */
export function startDragPreview(event: DragEvent, sourceEl: HTMLElement) {
  const dataTransfer = event.dataTransfer
  if (!dataTransfer) return

  // If a previous drag's end events never fired (possible when its source element was
  // detached mid-drag, leaving dragend no path to the window), repair the leak now
  // rather than stacking listeners and body classes.
  activeCleanup?.()

  dataTransfer.setDragImage(getSpacer(), 0, 0)
  // Tiptap's NodeView.onDragStart (a React handler on the NodeViewWrapper, which runs
  // after this descendant's handler) would replace our transparent drag image with its
  // own plain clone. Swallow that call; the dataTransfer only lives for this drag.
  dataTransfer.setDragImage = () => {}

  const rect = sourceEl.getBoundingClientRect()
  const grabX = event.clientX - rect.left
  const grabY = event.clientY - rect.top

  const preview = document.createElement("div")
  preview.className = styles.dragPreview
  // Paint that only makes sense in place — the card's edge shading over the drag spine,
  // say — keys on this to opt out of the clone, where the thing it related to is gone.
  preview.dataset.dragPreview = ""

  // Recreate the editor's class scope around the clone, since the preview lives outside
  // it: the scope classes provide the editor's descendant rules (heading sizes, list
  // styles — unified-tiptap.css), while the copied computed styles provide the values the
  // editor inherits from its own ancestors (app font-family, text color). Both are
  // needed; neither subsumes the other.
  const editorRoot = sourceEl.closest(".unified-tiptap-component")
  const scope = document.createElement("div")
  if (editorRoot) {
    scope.className = editorRoot.className
    const editorStyle = getComputedStyle(editorRoot)
    preview.style.fontFamily = editorStyle.fontFamily
    preview.style.color = editorStyle.color
  }
  const clone = sourceEl.cloneNode(true) as HTMLElement
  scope.appendChild(clone)
  preview.appendChild(scope)
  // A titled card travels as its title alone: mid-drag the preview is a handle, and a full
  // card in miniature hides the planner underneath it.
  if (clone.querySelector(".node-cardTitle")) preview.classList.add(styles.titleOnly)
  document.body.appendChild(preview)

  // Nodes whose visible content doesn't fill their wrapper (e.g. images sized
  // fit-content) mark the visual box with data-drag-preview-size; size the preview to
  // it instead of the wrapper so the card hugs the content. Ignore markers inside
  // nested node views (dragging a card shouldn't shrink to an image inside it).
  const marked = sourceEl.querySelector<HTMLElement>("[data-drag-preview-size]")
  const sizeEl = marked && marked.closest("[data-node-view-wrapper]") === sourceEl ? marked : null

  const previewStyle = getComputedStyle(preview)
  const paddingX = parseFloat(previewStyle.paddingLeft) + parseFloat(previewStyle.paddingRight)
  let contentWidth: number
  if (sizeEl) {
    contentWidth = sizeEl.getBoundingClientRect().width
  } else {
    // The wrapper's own horizontal margins (e.g. the negative hover-gutter margin) apply
    // inside the preview too; include them so the clone lays out at the source's exact
    // width and text wraps identically.
    const sourceStyle = getComputedStyle(sourceEl)
    contentWidth =
      rect.width + parseFloat(sourceStyle.marginLeft) + parseFloat(sourceStyle.marginRight)
  }
  // Width includes the padding: box-sizing is border-box via Tailwind's preflight.
  preview.style.width = `${contentWidth + paddingX}px`

  // Where the clone actually sits inside the preview (padding, wrapper margins). Offset
  // the translate by it so the clone starts exactly on top of the source node, and tilt
  // around the grab point, like lifting a card by where you hold it.
  const offsetX = clone.offsetLeft
  const offsetY = clone.offsetTop
  preview.style.transformOrigin = `${grabX + offsetX}px ${grabY + offsetY}px`

  const toX = (clientX: number) => clientX - grabX - offsetX
  const toY = (clientY: number) => clientY - grabY - offsetY

  // Start exactly over the source node, then "pick the card up".
  animate(preview, { x: toX(event.clientX), y: toY(event.clientY) }, { duration: 0 })
  animate(preview, { rotate: ROTATION_DEGREES, scale: PICKUP_SCALE }, PICKUP_SPRING)

  document.body.classList.add(DRAGGING_CLASS)

  // Coalesce follow updates to one spring retarget per frame; dragover can fire more
  // often than the display refreshes.
  let rafId: number | null = null
  let lastClientX = 0
  let lastClientY = 0
  const onDragOver = (e: DragEvent) => {
    lastClientX = e.clientX
    lastClientY = e.clientY
    rafId ??= requestAnimationFrame(() => {
      rafId = null
      animate(preview, { x: toX(lastClientX), y: toY(lastClientY) }, FOLLOW_LOCKED)
    })
  }
  const removeListeners = () => {
    activeCleanup = null
    if (rafId !== null) cancelAnimationFrame(rafId)
    document.body.classList.remove(DRAGGING_CLASS)
    document.removeEventListener("dragover", onDragOver)
    window.removeEventListener("drop", onDrop, true)
    window.removeEventListener("dragend", onDragEnd, true)
    sourceEl.removeEventListener("dragend", onDragEnd)
  }
  // On drop, remove instantly so the preview doesn't double up with the inserted node.
  // (Listen for drop as well as dragend: when a drop moves the node, the source element
  // is destroyed and its dragend never reaches the window.)
  const onDrop = () => {
    removeListeners()
    preview.remove()
  }
  // Cancelled drag (Escape, or dropped somewhere invalid): fade out in place.
  const onDragEnd = () => {
    removeListeners()
    void animate(preview, { opacity: 0 }, { duration: 0.15, ease: "easeOut" }).then(() =>
      preview.remove(),
    )
  }
  document.addEventListener("dragover", onDragOver)
  window.addEventListener("drop", onDrop, true)
  window.addEventListener("dragend", onDragEnd, true)
  // dragend dispatches at the drag source, so a listener directly on it fires even if
  // the source is detached mid-drag (when the dispatch path no longer reaches window).
  sourceEl.addEventListener("dragend", onDragEnd)
  activeCleanup = () => {
    removeListeners()
    preview.remove()
  }
}
